import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { MapPin, Star, Clock, Users, Camera, Phone, Car, Heart } from "lucide-react";

const KankeshwariTemple = () => {
  const navigate = useNavigate();
  const templeImage = "/Indore/temples/kankeshwari0.jpg";
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    "name": "Kankeshwari Temple",
    "description": "Beautiful temple dedicated to Goddess Kankeshwari in Indore, known for its spiritual significance",
    "image": templeImage,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Kankeshwari Temple Area",
      "addressLocality": "Indore",
      "addressRegion": "Madhya Pradesh",
      "addressCountry": "India"
    },
    "openingHours": "Mo-Su 06:00-21:00",
    "priceRange": "Free",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.4",
      "reviewCount": "4800"
    }
  };

  const highlights = [
    {
      icon: <Heart className="w-6 h-6" />,
      title: "Goddess Kankeshwari",
      description: "Sacred temple dedicated to Goddess Kankeshwari, the divine protector"
    },
    {
      icon: <Star className="w-6 h-6" />,
      title: "Spiritual Atmosphere",
      description: "Peaceful environment perfect for meditation and spiritual reflection"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Devotional Center",
      description: "Popular destination for devotees seeking protection and blessings"
    },
    {
      icon: <Camera className="w-6 h-6" />,
      title: "Cultural Heritage",
      description: "Showcasing traditional temple architecture and cultural significance"
    }
  ];

  const visitInfo = [
    { label: "Entry Fee", value: "Free for all visitors" },
    { label: "Timings", value: "6:00 AM - 9:00 PM (All days)" },
    { label: "Aarti Times", value: "Morning 7 AM, Evening 7 PM" },
    { label: "Best Time", value: "Early morning or evening" },
    { label: "Duration", value: "30 minutes - 1 hour" },
    { label: "Facilities", value: "Parking, Prasad, Drinking water" }
  ];

  const festivals = [
    { name: "Navratri", time: "March/October", special: "Grand celebrations with special prayers" },
    { name: "Durga Puja", time: "September/October", special: "Special offerings and celebrations" },
    { name: "Diwali", time: "October/November", special: "Beautiful decorations and celebrations" },
    { name: "Akshaya Tritiya", time: "April/May", special: "Special offerings and blessings" }
  ];

  const nearbyAttractions = [
    { name: "Khajrana Ganesh Temple", distance: "3 km", time: "10 mins" },
    { name: "Rajwada Palace", distance: "5 km", time: "15 mins" },
    { name: "Kanch Mandir", distance: "4 km", time: "12 mins" },
    { name: "Lal Bagh Palace", distance: "6 km", time: "18 mins" }
  ];

  return (
    <>
      <Helmet>
        <title>Kankeshwari Temple Indore - Goddess Kankeshwari | Aaoghumen</title>
        <meta name="description" content="Visit Kankeshwari Temple in Indore with Aaoghumen - temple dedicated to Goddess Kankeshwari. Complete guide with timings, festivals, and spiritual significance." />
        <meta name="keywords" content="kankeshwari temple indore, kankeshwari temple timings, indore temples, goddess kankeshwari, aaoghumen kankeshwari, spiritual places indore" />

        <meta property="og:title" content="Kankeshwari Temple Indore - Divine Protection | Aaoghumen" />
        <meta property="og:description" content="Experience divine protection at Kankeshwari Temple, dedicated to Goddess Kankeshwari in Indore." />
        <meta property="og:image" content={templeImage} />
        <meta name="twitter:image" content={templeImage} />
        <meta property="og:url" content="https://aaoghumen.com/destinations/indore/kankeshwari-temple" />

        <link rel="canonical" href="https://aaoghumen.com/destinations/indore/kankeshwari-temple" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-rose-50">
        {/* Hero Section */}
        <div className="relative h-96 bg-gradient-to-r from-pink-600 via-rose-500 to-red-600 overflow-hidden">
          <img
            src={templeImage}
            alt="Kankeshwari Temple Indore"
            className="absolute inset-0 w-full h-full object-cover object-center opacity-70"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="relative container mx-auto px-4 h-full flex items-center">
            <div className="text-white max-w-3xl">
              <h1 className="text-5xl font-bold mb-4">
                <span className="text-pink-300">Kankeshwari Temple</span> - Indore
              </h1>
              <p className="text-xl mb-6">
                Seek protection and blessings from Goddess Kankeshwari at this sacred temple.
              </p>
              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <Star className="w-5 h-5 text-yellow-400 mr-2" />
                  <span>4.4 Rating</span>
                </div>
                <div className="flex items-center">
                  <Clock className="w-5 h-5 mr-2" />
                  <span>6 AM - 9 PM</span>
                </div>
                <div className="flex items-center">
                  <Users className="w-5 h-5 mr-2" />
                  <span>Free Entry</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-12">
          {/* About Section */}
          <section className="mb-16">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">About Kankeshwari Temple</h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                Kankeshwari Temple in Indore is a revered spiritual destination dedicated to Goddess Kankeshwari, the divine protector. The temple holds special significance in Hindu culture and attracts thousands of devotees who come seeking blessings for protection, strength, and spiritual enlightenment.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                The temple features beautiful traditional architecture with intricate carvings and decorations that create a serene and spiritual atmosphere. The peaceful environment makes it an ideal place for meditation, prayer, and spiritual reflection.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                With Aaoghumen, discover the spiritual significance of Goddess Kankeshwari, participate in traditional rituals, and experience the temple's peaceful energy.
              </p>
            </div>
          </section>

          {/* Highlights */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Temple Highlights</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {highlights.map((highlight, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                  <div className="flex items-center mb-4">
                    <div className="bg-pink-100 p-3 rounded-lg mr-4">
                      {highlight.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">{highlight.title}</h3>
                  </div>
                  <p className="text-gray-600">{highlight.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Visit Information */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Visitor Information</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {visitInfo.map((info, index) => (
                  <div key={index} className="border-l-4 border-pink-500 pl-4">
                    <h3 className="font-bold text-gray-800 mb-2">{info.label}</h3>
                    <p className="text-gray-600">{info.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Festivals and Celebrations */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Festivals & Special Occasions</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {festivals.map((festival, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-xl font-bold text-pink-600 mb-2">{festival.name}</h3>
                  <p className="text-gray-500 mb-3">{festival.time}</p>
                  <p className="text-gray-600">{festival.special}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Nearby Attractions */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Nearby Attractions</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {nearbyAttractions.map((attraction, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 cursor-pointer">
                  <h3 className="text-lg font-bold text-gray-800 mb-2">{attraction.name}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-4 h-4 mr-2 text-pink-500" />
                      <span>{attraction.distance}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Car className="w-4 h-4 mr-2 text-blue-500" />
                      <span>{attraction.time}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact and Plan Visit */}
          <section className="bg-gradient-to-r from-pink-600 to-rose-600 rounded-2xl shadow-lg p-8 text-white">
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-4">Seek Divine Protection</h2>
              <p className="text-xl mb-6">Visit Kankeshwari Temple with spiritual guidance</p>
              <div className="flex justify-center items-center space-x-8 mb-6">
                <div className="flex items-center">
                  <Phone className="w-5 h-5 mr-2" />
                  <span>+91 97139 08893</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="w-5 h-5 mr-2" />
                  <span>Kankeshwari Temple Area, Indore</span>
                </div>
              </div>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => navigate('/onboarding')}
                  className="bg-white text-pink-600 font-bold py-3 px-6 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Plan Temple Visit
                </button>
                <button
                  onClick={() => navigate('/onboarding')}
                  className="border-2 border-white text-white font-bold py-3 px-6 rounded-full hover:bg-white hover:text-pink-600 transition-colors"
                >
                  Get Directions
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default KankeshwariTemple;